import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  GETQUICKITEMS,
  GET_SUPPLIER_FEW_DATA,
  SETRESTOSTOCKENTRIES,
  SUCCESS,
  SUPMGMT,
  formatDateInSnp,
  getAuthSession,
  getPreUrlAccordingToCart,
  isFeatureAllowed,
} from "../../constants/constant";
import utilities from "../../utilities/utilities";
import DialogBox from "../global/DialogBox";
import DismissButton from "../global/DismissButton";
import SnpButton from "../global/SnpButton";
import { snpAxios } from "../global/api";
import {
  calculateMainQuantityFromSmall,
  calculatePredictiveSmallQuantity,
  isValidRestoQuantityInput,
} from "../resturant/restoStockQuantity";
import "./GlobalRestoBulkStock.css";

let rowSequence = 0;
const createRow = () => ({
  key: `bulk-stock-${Date.now()}-${rowSequence++}`,
  query: "",
  options: [],
  loading: false,
  rateCard: null,
  quantity: "",
  smallQuantity: "",
});

const INITIAL_FORM = {
  billNo: "",
  supplierId: "",
  mfgDate: "",
  expiryDate: "",
  purchasePrice: "",
  description: "",
};

const conversionOf = (row) => row.rateCard?.conversionType || null;

const rateCardProductName = (rateCard) => rateCard.productName || rateCard.details || rateCard.name || "Rate Card";

const rateCardLabel = (rateCard) => {
  if (rateCard.productName && rateCard.foodQuantityName)
    return `${rateCard.productName} (${rateCard.foodQuantityName})`;
  return rateCard.details || rateCard.productName || rateCard.foodQuantityName || rateCard.name || "";
};

const GlobalRestoBulkStock = ({ open, onClose, onSaved, baseUrl, shopHash, cartType }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [rows, setRows] = useState([createRow()]);
  const [suppliers, setSuppliers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [metaExpanded, setMetaExpanded] = useState(true);
  const [, setOverlayPositionVersion] = useState(0);
  const searchTimers = useRef(new Map());
  const searchRequests = useRef(new Map());
  const searchAnchors = useRef(new Map());

  useEffect(() => () => {
    searchTimers.current.forEach((timer) => window.clearTimeout(timer));
    searchTimers.current.clear();
    searchRequests.current.clear();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const updateOverlayPosition = () => setOverlayPositionVersion((current) => current + 1);
    window.addEventListener("resize", updateOverlayPosition);
    window.addEventListener("scroll", updateOverlayPosition, true);
    return () => {
      window.removeEventListener("resize", updateOverlayPosition);
      window.removeEventListener("scroll", updateOverlayPosition, true);
    };
  }, [open]);

  useEffect(() => {
    searchTimers.current.forEach((timer) => window.clearTimeout(timer));
    searchTimers.current.clear();
    searchRequests.current.clear();
    if (!open) return;
    setForm(INITIAL_FORM);
    setRows([createRow()]);
    setError("");
    setSubmitting(false);
    setMetaExpanded(true);
    if (!isFeatureAllowed(SUPMGMT)) {
      setSuppliers([]);
      return;
    }
    let active = true;
    snpAxios.post(`${baseUrl}${getPreUrlAccordingToCart(cartType)}${GET_SUPPLIER_FEW_DATA}`, {
      jcJson: { apiToken: getAuthSession().apiToken, shopHash },
    }).then((response) => {
      if (active) setSuppliers(response.data.status === SUCCESS ? response.data.jcJson.value_2 || [] : []);
    }).catch(() => {
      if (active) setSuppliers([]);
    });
    return () => { active = false; };
  }, [baseUrl, cartType, open, shopHash]);

  const updateRow = (key, changes) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...changes } : row));
  };

  const searchRateCards = (key, value) => {
    updateRow(key, { query: value, rateCard: null, options: [], quantity: "", smallQuantity: "" });
    const previousTimer = searchTimers.current.get(key);
    if (previousTimer) window.clearTimeout(previousTimer);
    const requestId = (searchRequests.current.get(key) || 0) + 1;
    searchRequests.current.set(key, requestId);
    if (value.trim().length < 3) {
      updateRow(key, { loading: false });
      return;
    }
    updateRow(key, { loading: true });
    searchTimers.current.set(key, window.setTimeout(async () => {
      try {
        const encodedHash = utilities.encode(shopHash);
        const response = await snpAxios.get(
          `${baseUrl}${GETQUICKITEMS}?s=${encodedHash}&q=${encodeURIComponent(value.trim())}&m=1&stockVisible=-1&includeConversionDetails=true`,
        );
        if (searchRequests.current.get(key) !== requestId) return;
        updateRow(key, {
          loading: false,
          options: response.data.status === SUCCESS ? response.data.jcJson.value_2 || [] : [],
        });
      } catch (searchError) {
        if (searchRequests.current.get(key) === requestId)
          updateRow(key, { loading: false, options: [] });
      }
    }, 300));
  };

  const selectRateCard = (key, rateCard) => {
    const duplicate = rows.some((row) => row.key !== key && row.rateCard?.id === rateCard.id);
    if (duplicate) {
      setError("The same Rate Card cannot be added twice.");
      return;
    }
    setError("");
    updateRow(key, {
      query: rateCardLabel(rateCard),
      rateCard,
      options: [],
      quantity: "",
      smallQuantity: "",
    });
  };

  const addRow = () => {
    setRows((current) => [...current, createRow()]);
  };

  const removeRow = (key) => {
    const timer = searchTimers.current.get(key);
    if (timer) window.clearTimeout(timer);
    searchTimers.current.delete(key);
    searchRequests.current.delete(key);
    searchAnchors.current.delete(key);
    setRows((current) => current.length === 1
      ? [createRow()]
      : current.filter((row) => row.key !== key));
  };

  const renderRateCardOptions = (row) => {
    if (!row.options.length || typeof document === "undefined") return null;
    const anchor = searchAnchors.current.get(row.key);
    if (!anchor) return null;
    const rect = anchor.getBoundingClientRect();
    const availableBelow = window.innerHeight - rect.bottom - 12;
    const availableAbove = rect.top - 12;
    const openAbove = availableBelow < 180 && availableAbove > availableBelow;
    const maxHeight = Math.max(120, Math.min(300, openAbove ? availableAbove : availableBelow));
    const top = openAbove ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4;
    const overlayHost = anchor.closest(".UiDialog-root") || document.body;
    return createPortal(<div
      className="bulk-resto-stock__options"
      role="listbox"
      style={{ left: rect.left, top, width: rect.width, maxHeight }}
    >
      {row.options.map((option) => <button
        key={option.id}
        aria-selected="false"
        onClick={() => selectRateCard(row.key, option)}
        role="option"
        type="button"
      >
        <strong>{rateCardProductName(option)}</strong>
        {option.foodQuantityName ? <span>{option.foodQuantityName}</span> : null}
        <small>{option.liveStockCount ? `Stock: ${option.liveStockCount}` : "No current stock"}</small>
      </button>)}
    </div>, overlayHost);
  };

  const validate = () => {
    if (Number(form.purchasePrice) <= 0) return "Enter Purchase Price greater than zero.";
    if (!rows.length) return "Add at least one stock row.";
    const selectedIds = new Set();
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const conversion = conversionOf(row);
      if (!row.rateCard || !conversion?.cnvId) return `Select a Rate Card in row ${index + 1}.`;
      if (selectedIds.has(row.rateCard.id)) return `Rate Card is duplicated in row ${index + 1}.`;
      selectedIds.add(row.rateCard.id);
      const quantity = Number(row.quantity || 0);
      const smallQuantity = Number(row.smallQuantity || 0);
      if (!conversion.unitRestrictions && Number(conversion.smallUnitCount) <= 0)
        return `Small-unit conversion is invalid in row ${index + 1}.`;
      if (quantity < 0 || smallQuantity < 0 || quantity + smallQuantity <= 0)
        return `Enter a quantity greater than zero in row ${index + 1}.`;
      if (conversion.unitRestrictions && smallQuantity > 0)
        return `Small quantity is unavailable in row ${index + 1}.`;
      if (!conversion.unitRestrictions && conversion.decimalSmRestrictions && !Number.isInteger(smallQuantity))
        return `Small quantity must be a whole number in row ${index + 1}.`;
      if (!conversion.unitRestrictions && conversion.decimalSmRestrictions && !Number.isInteger(quantity))
        return `Main quantity must be a whole number in row ${index + 1}.`;
    }
    return "";
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await snpAxios.post(`${baseUrl}${SETRESTOSTOCKENTRIES}`, {
        jcJson: {
          apiToken: getAuthSession().apiToken,
          shopHash,
          ...form,
          purchasePrice: Number(form.purchasePrice),
          mfgDate: formatDateInSnp(form.mfgDate, ""),
          expiryDate: formatDateInSnp(form.expiryDate, ""),
          entries: rows.map((row) => ({
            rateCardId: row.rateCard.id,
            quantity: Number(row.quantity || 0),
            smallQuantity: Number(row.smallQuantity || 0),
            predictiveSmallQuantity: conversionOf(row)?.unitRestrictions
              ? 0 : Number(row.smallQuantity || 0),
          })),
        },
      });
      if (response.data.status !== SUCCESS) {
        setError(response.data.errorMessage || "Unable to add stock entries.");
        return;
      }
      const savedCount = response.data.jcJson.savedCount || rows.length;
      onClose();
      onSaved(savedCount);
    } catch (submitError) {
      setError(submitError.response?.data?.errorMessage || "Unable to add stock entries.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = <div className="bulk-resto-stock">
    <div className={`bulk-resto-stock__meta-panel${metaExpanded ? " is-expanded" : ""}`}>
      <div className="bulk-resto-stock__meta-heading">
        <strong>Purchase details</strong>
        <button
          aria-expanded={metaExpanded}
          aria-controls="bulk-resto-stock-purchase-details"
          onClick={() => setMetaExpanded((current) => !current)}
          type="button"
        >
          <span aria-hidden="true">{metaExpanded ? "⌃" : "⌄"}</span>
          {metaExpanded ? "Hide details" : "Show details"}
        </button>
      </div>
      {metaExpanded ? <div className="bulk-resto-stock__meta" id="bulk-resto-stock-purchase-details">
        <label><span>Bill Number</span><input value={form.billNo} onChange={(event) => setForm({ ...form, billNo: event.target.value })} /></label>
        {isFeatureAllowed(SUPMGMT) ? <label><span>Supplier</span><select value={form.supplierId} onChange={(event) => setForm({ ...form, supplierId: event.target.value })}>
          <option value="">Select supplier</option>
          {suppliers.map((supplier) => <option key={supplier.supplierId} value={supplier.supplierId}>{supplier.supplierName}</option>)}
        </select></label> : null}
        <label><span>Manufacture Date</span><input type="date" value={form.mfgDate} onChange={(event) => setForm({ ...form, mfgDate: event.target.value })} /></label>
        <label><span>Expiry Date</span><input type="date" value={form.expiryDate} onChange={(event) => setForm({ ...form, expiryDate: event.target.value })} /></label>
        <label><span>Purchase Price *</span><input min="0" step="any" type="number" value={form.purchasePrice} onChange={(event) => setForm({ ...form, purchasePrice: event.target.value })} /></label>
        <label className="bulk-resto-stock__description"><span>Description</span><textarea rows="3" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
      </div> : null}
    </div>

    <div className="bulk-resto-stock__sheet" role="table" aria-label="Bulk restaurant stock entries">
      <div className="bulk-resto-stock__header" role="row">
        <span>Rate Card search</span><span>Main quantity</span><span>Small quantity</span><span>Rows</span>
      </div>
      {rows.map((row, index) => {
        const conversion = conversionOf(row);
        const smallDisabled = !conversion || conversion.unitRestrictions || Number(conversion.smallUnitCount) <= 0;
        return <div className="bulk-resto-stock__row" role="row" key={row.key}>
          <div
            className="bulk-resto-stock__search"
            ref={(element) => {
              if (element) searchAnchors.current.set(row.key, element);
              else searchAnchors.current.delete(row.key);
            }}
          >
            {row.rateCard ? <div className="bulk-resto-stock__selected-rate-card">
              <span className="bulk-resto-stock__rate-card-copy">
                <strong>{rateCardProductName(row.rateCard)}</strong>
                {row.rateCard.foodQuantityName ? <span>{row.rateCard.foodQuantityName}</span> : null}
                <small>{row.rateCard.liveStockCount ? `Stock: ${row.rateCard.liveStockCount}` : "No current stock"}</small>
              </span>
              <button
                aria-label={`Change Rate Card in row ${index + 1}`}
                onClick={() => updateRow(row.key, { query: "", rateCard: null, quantity: "", smallQuantity: "" })}
                title="Change Rate Card"
                type="button"
              >×</button>
            </div> : <input
                aria-label={`Rate Card search row ${index + 1}`}
                autoComplete="off"
                placeholder="Type 3 letters to search Rate Cards"
                value={row.query}
                onChange={(event) => searchRateCards(row.key, event.target.value)}
              />}
            {row.loading && !row.rateCard ? <small className="bulk-resto-stock__searching">Searching…</small> : null}
            {renderRateCardOptions(row)}
          </div>
          <label><span>{conversion?.unitName || "Main unit"}</span><input
            aria-label={`Main quantity row ${index + 1}`}
            disabled={!conversion}
            inputMode={conversion?.decimalSmRestrictions ? "numeric" : "decimal"}
            value={row.quantity}
            onChange={(event) => {
              const value = event.target.value;
              if (!isValidRestoQuantityInput(value, conversion?.decimalSmRestrictions)) return;
              if (conversion?.unitRestrictions) {
                updateRow(row.key, { quantity: value, smallQuantity: "" });
                return;
              }
              updateRow(row.key, {
                quantity: value,
                smallQuantity: calculatePredictiveSmallQuantity(value, conversion?.smallUnitCount),
              });
            }}
          /></label>
          <label><span>{conversion?.smallUnitName || "Small unit"}</span><input
            aria-label={`Small quantity row ${index + 1}`}
            disabled={smallDisabled}
            inputMode={conversion?.decimalSmRestrictions ? "numeric" : "decimal"}
            value={smallDisabled ? "" : row.smallQuantity}
            onChange={(event) => {
              const value = event.target.value;
              if (!isValidRestoQuantityInput(value, conversion?.decimalSmRestrictions)) return;
              updateRow(row.key, {
                quantity: calculateMainQuantityFromSmall(
                  value, conversion?.smallUnitCount, conversion?.decimalSmRestrictions),
                smallQuantity: value,
              });
            }}
          /></label>
          <div className="bulk-resto-stock__row-actions">
            <button type="button" title="Add another row" aria-label="Add another stock row" onClick={addRow}>+</button>
            <button type="button" title="Remove row" aria-label={`Remove stock row ${index + 1}`} onClick={() => removeRow(row.key)}>−</button>
          </div>
        </div>;
      })}
    </div>
    {error ? <div className="bulk-resto-stock__error" role="alert">{error}</div> : null}
  </div>;

  const actions = <>
    <SnpButton disabled={submitting} variant="contained" color="primary" onClick={submit}>
      {submitting ? "Saving…" : `Add ${rows.length} Stock ${rows.length === 1 ? "Entry" : "Entries"}`}
    </SnpButton>
    <DismissButton disabled={submitting} variant="contained" color="primary" onClick={onClose}>Dismiss</DismissButton>
  </>;

  return <DialogBox
    showDialog={open}
    title="Bulk Add Restaurant Stock"
    onClose={onClose}
    outsideClickedDismiss={!submitting}
    dialogContent={content}
    dialogActions={actions}
    mwidth="xl"
    mFullWidth
  />;
};

export default GlobalRestoBulkStock;
